import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Filters from '../../relevanter/Filters';
import { FiltersState } from '../../hooks/useFilters';
import SpecializationModal from '../ui/SpecializationModal';
import IndustryModal from '../ui/IndustryModal';
import AreaModal from '../ui/AreaModal';
import SearchMultiSelectModal from '../ui/SearchMultiSelectModal';
import { fetchHHCountries, type CountryOption } from '../../services/hhDictionariesService';
import { getSourceConfig } from '../../sources';

interface FiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FiltersState;
  onFiltersChange: (filters: Partial<FiltersState>) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  searchMode?: string;
  onSearchModeChange?: (mode: string) => void;
  shouldAutoParse?: boolean;
  parsedSummary?: any;
  setParsedSummary?: (summary: any) => void;
  scrollToSection?: string;
}

export const FiltersModal: React.FC<FiltersModalProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onSearch,
  onClearFilters,
  searchMode,
  onSearchModeChange,
  shouldAutoParse,
  parsedSummary,
  setParsedSummary,
  scrollToSection
}) => {
  const [isSpecializationModalOpen, setIsSpecializationModalOpen] = useState(false);
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [isCitizenshipModalOpen, setIsCitizenshipModalOpen] = useState(false);
  const [countries, setCountries] = useState<CountryOption[]>([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIndustries((filters as any).selectedIndustries || []);
      setSelectedSpecializations((filters as any).selectedSpecializations || []);
      setSelectedAreas(filters.cityIds || []);
    }
  }, [isOpen, filters]);

  // Гражданство: подгружаем словарь из sourceConfig.dataLoaders.nationality
  // если он задан (Avito → fetchAvitoNationalities → catalog_nationality.xml).
  // Иначе fallback на fetchHHCountries (HH/E-Staff legacy-поведение).
  // При переключении source — словарь подгружается заново.
  useEffect(() => {
    const nationalityLoader = getSourceConfig(filters.source || 'hh').dataLoaders?.nationality;
    if (nationalityLoader) {
      nationalityLoader()
        .then((items) => setCountries(items.map((i) => ({ id: i.id, name: i.name } as CountryOption))))
        .catch(() => setCountries([]));
    } else {
      fetchHHCountries()
        .then(setCountries)
        .catch(() => setCountries([]));
    }
  }, [filters.source]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998] animate-in fade-in duration-300" 
        onClick={onClose}
      />
      
      {/* Side Modal */}
      <div className="fixed inset-0 z-[9999] flex justify-end p-3">
        <div className="bg-white w-full max-w-lg h-full shadow-2xl overflow-hidden rounded-[24px] transform transition-transform duration-300 ease-in-out animate-in slide-in-from-right">
          <Filters
            value={filters}
            onChange={onFiltersChange}
            onSearch={() => {
              onClose();
              onSearch();
            }}
            onClose={onClose}
            isSpecializationModalOpen={isSpecializationModalOpen}
            onSpecializationModalOpen={() => setIsSpecializationModalOpen(true)}
            onSpecializationModalClose={() => setIsSpecializationModalOpen(false)}
            selectedSpecializations={selectedSpecializations}
            onSpecializationsChange={setSelectedSpecializations}
            isIndustryModalOpen={isIndustryModalOpen}
            onIndustryModalOpen={() => setIsIndustryModalOpen(true)}
            onIndustryModalClose={() => setIsIndustryModalOpen(false)}
            selectedIndustries={selectedIndustries}
            onIndustriesChange={setSelectedIndustries}
            onAreaModalOpen={() => setIsAreaModalOpen(true)}
            onCitizenshipModalOpen={() => setIsCitizenshipModalOpen(true)}
            scrollToSection={scrollToSection}
          />
        </div>
      </div>
      
      {/* Specialization Modal - рендерится на уровне всей страницы.
          Данные берутся через sourceConfig.dataLoaders.specialization,
          режим (иерархия/плоский) — через sourceConfig.uiHints.specializationMode.
          Это позволяет одной модалке работать с любым source без if-ов. */}
      <SpecializationModal
        isOpen={isSpecializationModalOpen}
        onClose={() => setIsSpecializationModalOpen(false)}
        selectedSpecializations={selectedSpecializations}
        onSelectionChange={(vals) => {
          setSelectedSpecializations(vals);
          onFiltersChange({ selectedSpecializations: vals });
        }}
        fetchData={getSourceConfig(filters.source || 'hh').dataLoaders?.specialization}
        mode={getSourceConfig(filters.source || 'hh').uiHints?.specializationMode}
      />

      {/* Industry Modal - рендерится на уровне всей страницы */}
      <IndustryModal
        isOpen={isIndustryModalOpen}
        onClose={() => setIsIndustryModalOpen(false)}
        selectedIndustries={selectedIndustries}
        experiencePeriod={filters?.experiencePeriod || ''}
        onSelectionChange={(vals, _filterByExp, expPeriod) => {
          setSelectedIndustries(vals);
          onFiltersChange({ selectedIndustries: vals, ...(expPeriod !== undefined ? { experiencePeriod: expPeriod } : {}) });
        }}
      />

      {/* Area Modal — выбор географии (регион/город). Данные берутся
          из sourceConfig.dataLoaders.locations: для hh — undefined →
          fallback на fetchHHAreasTree (legacy-поведение), для avito —
          fetchAvitoLocations (наш /api/avito/dictionaries/locations →
          распарсенный Locations.xml). Так одна модалка переиспользуется
          для всех источников без if-ов в самом компоненте.

          Выбранные ID сохраняются в FiltersState.cityIds. searchAvitoResumes
          берёт cityIds[0] и передаёт как ?location=<id> в /api/avito/search. */}
      <AreaModal
        isOpen={isAreaModalOpen}
        onClose={() => setIsAreaModalOpen(false)}
        selectedAreas={selectedAreas}
        fetchData={getSourceConfig(filters.source || 'hh').dataLoaders?.locations}
        hideCountryTabs={filters.source === 'avito'}
        onApply={async (areas: string[]) => {
          // Пост-обработка зависит от source: для hh — compressCityIds
          // (свернуть города в регион, Москва+МО), для остальных — identity.
          // Конкретная реализация приходит из sourceConfig.postProcessors,
          // компонент об источнике не знает.
          const sourceConfig = getSourceConfig(filters.source || 'hh');
          const compressor = sourceConfig.postProcessors?.compressCityIds
            ?? (async (ids: string[]) => ids);
          const resolveName = sourceConfig.postProcessors?.getAreaNameById
            ?? (async (id: string) => id);
          const compressed = await compressor(areas);
          setSelectedAreas(compressed);
          const names = await Promise.all(
            compressed.map(async (id: string) => {
              try { return await resolveName(id); } catch { return id; }
            })
          );
          onFiltersChange({ cityIds: compressed, cities: names });
        }}
      />

      {/* Citizenship Modal — множественный выбор гражданства */}
      <SearchMultiSelectModal
        isOpen={isCitizenshipModalOpen}
        onClose={() => setIsCitizenshipModalOpen(false)}
        title="Гражданство"
        items={countries}
        selectedItems={Array.isArray(filters.citizenship) ? filters.citizenship : []}
        onSelect={(items) => onFiltersChange({ citizenship: items } as Partial<FiltersState>)}
        searchPlaceholder="Поиск страны..."
      />

    </>
  );
};

export default FiltersModal;
